import { api } from "./api.ts";
import type { MiscServiceType, UserType } from "types/DataTypes";

type GetMiscServiceByIdArgs = {
  id: string;
};

type PatchMiscServiceArgs = {
  id: string;
  data: Partial<MiscServiceType>;
};

type UploadOrganizationLetterheadArgs = {
  file: File;
};

type UpdateOrganizationSettingsArgs = {
  data: Partial<OrganizationSettingsResponse>;
};

export type OrganizationSettingsResponse = {
  id: number;
  organization: number;
  name: string;
  slug: string;
  document_letterhead_s3_key?: string;
  msa_acknowledgement_contact_title?: string;
  msa_acknowledgement_contact_name?: string;
  msa_acknowledgement_contact_phone?: string;
  mcp_acknowledgement_contact_title?: string;
  mcp_acknowledgement_contact_name?: string;
  mcp_acknowledgement_contact_phone?: string;
  legal_name?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  claim_number_prefix?: string;
  quickbooks_integration_enabled?: boolean;
  metadoc_indexing_enabled?: boolean;
  allow_mp4_uploads?: boolean;
};

export type UploadLetterheadResponse = {
  message: string;
  s3_key: string;
};

export const miscAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getConstants: build.query<unknown, void>({
      query: () => ({
        url: `api/v1/constants/`,
        method: "GET",
      }),
    }),
    getMe: build.query<UserType, void>({
      query: () => ({
        url: `api/v1/me/`,
        method: "GET",
      }),
    }),
    getMiscServiceById: build.query<MiscServiceType, GetMiscServiceByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/miscellaneous-services/${id}/`,
        method: "GET",
      }),
      providesTags: ["MiscService"],
    }),
    patchMiscServiceApiById: build.mutation<
      MiscServiceType,
      PatchMiscServiceArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/miscellaneous-services/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["MiscService"],
    }),
    getOrganizationSettings: build.query<OrganizationSettingsResponse, void>({
      query: () => ({
        url: `api/v1/organization-settings/`,
        method: "GET",
      }),
      providesTags: ["OrganizationSettings"],
    }),
    uploadOrganizationLetterhead: build.mutation<
      UploadLetterheadResponse,
      UploadOrganizationLetterheadArgs
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `api/v1/organization-letterhead/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["OrganizationSettings"],
    }),
    deleteOrganizationLetterhead: build.mutation<{ message: string }, void>({
      query: () => ({
        url: `api/v1/organization-letterhead/`,
        method: "DELETE",
      }),
      invalidatesTags: ["OrganizationSettings"],
    }),
    updateOrganizationSettings: build.mutation<
      OrganizationSettingsResponse,
      UpdateOrganizationSettingsArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/organization-settings/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["OrganizationSettings"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetConstantsQuery,
  useGetMeQuery,
  useGetMiscServiceByIdQuery,
  usePatchMiscServiceApiByIdMutation,
  useGetOrganizationSettingsQuery,
  useUploadOrganizationLetterheadMutation,
  useDeleteOrganizationLetterheadMutation,
  useUpdateOrganizationSettingsMutation,
} = miscAPI;
